import { useState } from 'react';
import { Check, Plus, X, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Input } from '../../ui/input';
import { useToast } from '../../ui/use-toast';
import { supabase } from '../../../lib/supabase';
import { v4 as uuidv4 } from 'uuid';

interface Category {
  id: string;
  name: string;
}

interface BlogCategorySelectProps {
  categories: Category[];
  selectedCategories: string[];
  onChange: (selectedIds: string[]) => void;
}

export const BlogCategorySelect = ({
  categories,
  selectedCategories,
  onChange,
}: BlogCategorySelectProps) => {
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();

  const handleCategoryToggle = (categoryId: string) => {
    const updatedSelection = selectedCategories.includes(categoryId)
      ? selectedCategories.filter(id => id !== categoryId)
      : [...selectedCategories, categoryId];
    
    onChange(updatedSelection);
  };

  const createNewCategory = async () => {
    if (!newCategoryName.trim()) {
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Le nom de la catégorie ne peut pas être vide',
      });
      return;
    }

    setIsCreating(true);
    try {
      const categoryId = uuidv4();
      const slug = newCategoryName
        .toLowerCase()
        .replace(/[^\w\s]/gi, '')
        .replace(/\s+/g, '-');

      const { error } = await supabase.from('blog_categories').insert({
        id: categoryId,
        name: newCategoryName.trim(),
        slug: `${slug}-${categoryId.slice(0, 8)}`,
        created_at: new Date().toISOString(),
      });

      if (error) throw error;

      toast({
        title: 'Succès',
        description: 'Catégorie créée avec succès',
      });

      // Ajouter la nouvelle catégorie à la liste et la sélectionner
      const newCategory = {
        id: categoryId,
        name: newCategoryName.trim(),
      };
      
      // Mettre à jour la liste des catégories (simulé ici, normalement on rechargerait)
      categories.push(newCategory);
      
      // Sélectionner la nouvelle catégorie
      onChange([...selectedCategories, categoryId]);
      
      // Réinitialiser le formulaire
      setNewCategoryName('');
      setShowNewCategory(false);
    } catch (error) {
      console.error('Erreur lors de la création de la catégorie:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Impossible de créer la catégorie',
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      {categories.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-white mb-2">Aucune catégorie disponible</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowNewCategory(true)}
            className="text-yellow-500 border-yellow-500 hover:bg-yellow-500/10"
          >
            <Plus className="mr-2 h-4 w-4" />
            Créer une catégorie
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {categories.map((category) => (
            <div key={category.id} className="flex items-center space-x-2">
              <Checkbox
                id={`category-${category.id}`}
                checked={selectedCategories.includes(category.id)}
                onCheckedChange={() => handleCategoryToggle(category.id)}
              />
              <label
                htmlFor={`category-${category.id}`}
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer text-white"
              >
                {category.name}
              </label>
            </div>
          ))}
        </div>
      )}

      {!showNewCategory ? (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowNewCategory(true)}
        >
          <Plus className="mr-2 h-4 w-4" />
          Ajouter une catégorie
        </Button>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Input
              placeholder="Nom de la catégorie"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              className="flex-1 text-white bg-white/5 border-white/10 focus-visible:ring-yellow-500 focus-visible:ring-offset-0 focus-visible:ring-1"
            />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setShowNewCategory(false);
                setNewCategoryName('');
              }}
            >
              <X className="h-4 w-4 text-yellow-500 hover:text-yellow-400" />
            </Button>
          </div>
          <Button
            size="sm"
            onClick={createNewCategory}
            disabled={isCreating || !newCategoryName.trim()}
            className="bg-yellow-500 hover:bg-yellow-600 text-black"
          >
            {isCreating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Création...
              </>
            ) : (
              <>
                <Check className="mr-2 h-4 w-4" />
                Créer
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default BlogCategorySelect;
